import React, { useState } from 'react';
import { MessageCircle, Mail, Phone, MapPin, Clock, Send, Instagram, Facebook, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useSiteBrand } from '../components/BrandLogo';

export default function ContactoPage() {
  const brand = useSiteBrand();
  const siteName = brand?.site_name || 'NubesMTY';
  const waRaw = (brand?.whatsapp_number || brand?.whatsapp || '5218139147429').replace(/\D/g, '');
  const email = brand?.contact_email || brand?.support_email || 'contacto@nubesmty.com';
  const formatMx = (d) => `+52 ${d.slice(-10, -7)} ${d.slice(-7, -4)} ${d.slice(-4)}`;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const sendViaWhatsapp = (e) => {
    e.preventDefault();
    if (!name || !message) return;
    setSending(true);
    const text = `Hola ${siteName}, soy ${name}${phone ? ` (Tel: ${phone})` : ''}.\nAsunto: ${subject || 'Consulta'}\n\n${message}`;
    setTimeout(() => {
      window.open(`https://wa.me/${waRaw}?text=${encodeURIComponent(text)}`, '_blank');
      setSending(false);
      setSent(true);
      setName(''); setPhone(''); setSubject(''); setMessage('');
    }, 500);
  };

  return (
    <div className="min-h-[calc(100vh-160px)] pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300/90">Contacto</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
            Estamos <span className="text-orange-400">para ayudarte</span>
          </h1>
          <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Escríbenos por el canal que prefieras. En {siteName} respondemos en minutos por WhatsApp y en menos de 24 h por correo.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 grid md:grid-cols-5 gap-6 md:gap-8">
        {/* Left: contact channels */}
        <div className="md:col-span-2 space-y-4">
          <a
            href={`https://wa.me/${waRaw}`}
            target="_blank"
            rel="noreferrer"
            className="group block rounded-3xl border border-white/10 bg-gradient-to-br from-[#25D366]/10 to-transparent p-6 hover:border-[#25D366]/40 transition-colors"
            data-testid="contacto-whatsapp-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center shrink-0 group-hover:scale-105 transition">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#25D366]">Más rápido</span>
                <h3 className="text-lg font-black text-white mt-1">WhatsApp</h3>
                <p className="text-white/60 text-sm mt-1">{formatMx(waRaw)}</p>
                <p className="text-white/50 text-xs mt-2">Respuesta en minutos · Lun a Vie 10:00 – 17:00</p>
              </div>
            </div>
          </a>

          <a
            href={`mailto:${email}`}
            className="group block rounded-3xl border border-white/10 bg-gradient-to-br from-orange-500/10 to-transparent p-6 hover:border-orange-500/40 transition-colors"
            data-testid="contacto-email-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/15 text-orange-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition">
                <Mail className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-orange-400">Formal</span>
                <h3 className="text-lg font-black text-white mt-1">Correo</h3>
                <p className="text-white/60 text-sm mt-1 break-all">{email}</p>
                <p className="text-white/50 text-xs mt-2">Respuesta en menos de 24 h</p>
              </div>
            </div>
          </a>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-orange-400">
                <MapPin className="w-4 h-4" />
              </div>
              <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mt-3">Base</p>
              <p className="text-white font-bold text-sm mt-0.5">Monterrey, NL</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-orange-400">
                <Clock className="w-4 h-4" />
              </div>
              <p className="text-[11px] font-bold tracking-wider uppercase text-white/40 mt-3">Horario</p>
              <p className="text-white font-bold text-sm mt-0.5">Lun – Vie</p>
              <p className="text-white/50 text-xs mt-1">10:00 – 17:00 hrs</p>
            </div>
          </div>

          {/* Socials */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/40">Síguenos</p>
            <div className="flex items-center gap-2 mt-3">
              <a href={brand?.instagram_url || '#'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-orange-500 hover:text-black hover:border-orange-500 transition">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={brand?.facebook_url || '#'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-orange-500 hover:text-black hover:border-orange-500 transition">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="md:col-span-3">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              Envíanos un <span className="text-orange-400">mensaje</span>
            </h2>
            <p className="text-white/55 text-sm mt-1.5">Completa el formulario y te contactamos por WhatsApp con tu consulta lista.</p>

            {sent ? (
              <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 flex items-start gap-4 animate-in fade-in duration-500" data-testid="contacto-form-success">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5" />
                <div>
                  <h3 className="text-white font-black">Se abrió tu WhatsApp</h3>
                  <p className="text-white/60 text-sm mt-1">Tu mensaje ya está redactado. Presiona enviar en WhatsApp y te respondemos en minutos.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={sendViaWhatsapp} className="mt-6 grid md:grid-cols-2 gap-4" data-testid="contacto-form">
                <div className="md:col-span-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Nombre*</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    required
                    className="mt-2 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
                    data-testid="contacto-name-input"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Teléfono (opcional)</label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s]/g, ''))}
                    placeholder="+52 81 1234 5678"
                    className="mt-2 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
                    data-testid="contacto-phone-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Asunto</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ej: ¿Cuánto dura un desechable de 12k puffs?"
                    className="mt-2 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
                    data-testid="contacto-subject-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Mensaje*</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Cuéntanos con detalle: modelo del equipo, número de pedido, o tu consulta…"
                    required
                    rows={5}
                    className="mt-2 w-full rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 px-3 py-3 text-sm resize-none"
                    data-testid="contacto-message-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button
                    type="submit"
                    disabled={sending || !name || !message}
                    className="w-full h-13 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-black font-black shadow-lg shadow-[#25D366]/25 text-base"
                    data-testid="contacto-submit-btn"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparando…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Enviar por WhatsApp</>
                    )}
                  </Button>
                  <p className="text-center text-[11px] text-white/40 mt-3">
                    Al enviar aceptas nuestro <a href="#" className="underline hover:text-white/70">aviso de privacidad</a>. Tus datos no se comparten con terceros.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
